import { useParams, useNavigate } from 'react-router-dom';
import { insightsData } from '../../mocks/insights';
import { useState } from 'react';

export default function BoardPage() {
  const { category } = useParams<{ category: string }>();
  const navigate = useNavigate();
  const [subscriptionCode, setSubscriptionCode] = useState('');
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false);

  const categoryConfig: Record<string, { name: string; color: string; icon: string; description: string }> = {
    'core-loop': {
      name: '히트 코어루프',
      color: '#8b4513',
      icon: 'ri-fire-fill',
      description: '글로벌 히트작들의 핵심 코어루프를 분석하고 성공 요인을 파헤칩니다'
    },
    'fast-follower': {
      name: '패스트 팔로워',
      color: '#2f4f4f',
      icon: 'ri-rocket-fill',
      description: '베리에이션과 조합 전략으로 시장 기회를 포착하는 방법을 연구합니다'
    },
    'idea-archive': {
      name: '해든의 코어루프 아이디어 아카이빙',
      color: '#5a4a3a',
      icon: 'ri-gamepad-fill',
      description: '직접 플레이 가능한 게임 프로토타입과 개발 노트를 무료로 제공합니다'
    },
    'hit-prediction': {
      name: '히트 가능성 예측 리포트',
      color: '#4a3a5a',
      icon: 'ri-line-chart-fill',
      description: '데이터 기반 히트 예측과 장르별 성공 확률 분석을 독점 제공합니다'
    }
  };

  const currentCategory = categoryConfig[category || ''];
  const posts = insightsData.filter(post => post.category === category);
  const isPremium = category === 'hit-prediction';

  const handlePremiumClick = (postId: number) => {
    if (isUnlocked) {
      navigate(`/post/${postId}`);
    } else {
      setShowCodeInput(true);
    }
  };

  const handleCodeSubmit = () => {
    // 구독 코드: "해든"을 입력하면 잠금 해제
    if (subscriptionCode.trim() === '해든') {
      setIsUnlocked(true);
      setShowCodeInput(false);
      alert('프리미엄 콘텐츠가 잠금 해제되었습니다! 🎉');
    } else {
      alert('올바른 구독 코드를 입력해주세요.');
    }
  };

  if (!currentCategory) {
    return (
      <div className="min-h-screen bg-[#f5f1e8] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl text-[#4a3f33] mb-4">게시판을 찾을 수 없습니다</h1>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-[#8b4513] text-white rounded-lg hover:bg-[#654321] transition-colors cursor-pointer whitespace-nowrap"
          >
            홈으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f1e8]">
      {/* Header */}
      <div className="bg-white/60 border-b border-[#e8dfd0] sticky top-0 z-40 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-[#6b5d4f] hover:text-[#4a3f33] transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-xl"></i>
            <span className="text-sm">홈으로</span>
          </button>
          <h1 
            className="text-2xl font-light tracking-wide flex items-center gap-2"
            style={{ 
              fontFamily: 'Georgia, serif',
              color: currentCategory.color
            }}
          >
            <i className={`${currentCategory.icon} text-2xl`}></i>
            {currentCategory.name}
          </h1>
          <div className="w-20"></div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Category Description */}
        <div className="mb-12 text-center">
          <p className="text-lg text-[#6b5d4f] leading-relaxed">
            {currentCategory.description}
          </p>
        </div>

        {/* Posts Grid */}
        <div className="space-y-6">
          {posts.map((post) => (
            <article
              key={post.id}
              onClick={() => {
                if (isPremium && !isUnlocked) {
                  handlePremiumClick(post.id);
                } else {
                  navigate(`/post/${post.id}`);
                }
              }}
              className={`group cursor-pointer ${isPremium && !isUnlocked ? 'relative' : ''}`}
            >
              <div className={`bg-white/60 border border-[#e8dfd0] rounded-lg p-6 hover:bg-white hover:border-[#d4c4a8] hover:shadow-lg transition-all duration-300 ${isPremium && !isUnlocked ? 'opacity-75' : ''}`}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-sm text-[#9d8b6f]">{post.date}</span>
                  {isPremium && !isUnlocked && (
                    <>
                      <span className="text-sm text-[#b5a89a]">·</span>
                      <span className="ml-auto px-3 py-1 bg-[#4a3a5a] text-white text-xs rounded-full flex items-center gap-1 whitespace-nowrap">
                        <i className="ri-vip-crown-fill"></i>
                        프리미엄
                      </span>
                    </>
                  )}
                </div>
                <h2 className="text-2xl font-normal text-[#4a3f33] mb-3 leading-relaxed group-hover:text-[#6b5d4f] transition-colors duration-200">
                  {post.title}
                </h2>
                <p className="text-base text-[#8b7d6f] leading-relaxed">
                  {post.excerpt}
                </p>
              </div>
            </article>
          ))}
        </div>

        {/* Empty State */}
        {posts.length === 0 && (
          <div className="text-center py-20">
            <i className={`${currentCategory.icon} text-6xl text-[#d4c4a8] mb-4`}></i>
            <p className="text-lg text-[#8b7d6f]">아직 게시글이 없습니다</p>
          </div>
        )}
      </div>

      {/* Premium Code Input Modal */}
      {showCodeInput && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-[#f5f1e8] border-2 border-[#d4c4a8] rounded-lg p-8 max-w-md w-full shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-[#4a3a5a] to-[#3a2a4a] rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <i className="ri-line-chart-fill text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-light text-[#4a3f33] mb-2" style={{ fontFamily: 'Georgia, serif' }}>
                히트 예측 리포트 구독
              </h3>
              <p className="text-sm text-[#6b5d4f]">
                구독 코드를 입력하시면 데이터 기반 히트 예측과<br />장르별 성공 확률 분석을 확인하실 수 있습니다
              </p>
            </div>

            <div className="space-y-4">
              <input
                type="text"
                value={subscriptionCode}
                onChange={(e) => setSubscriptionCode(e.target.value)}
                placeholder="구독 코드를 입력하세요"
                className="w-full px-4 py-3 bg-white border border-[#e8dfd0] rounded-lg text-[#4a3f33] placeholder-[#9d8b6f] focus:outline-none focus:border-[#4a3a5a] transition-colors text-sm"
                onKeyPress={(e) => e.key === 'Enter' && handleCodeSubmit()}
              />

              <div className="flex gap-3">
                <button
                  onClick={() => setShowCodeInput(false)}
                  className="flex-1 px-4 py-3 bg-white border border-[#e8dfd0] rounded-lg text-[#6b5d4f] hover:bg-[#faf8f3] transition-colors text-sm whitespace-nowrap cursor-pointer"
                >
                  취소
                </button>
                <button
                  onClick={handleCodeSubmit}
                  className="flex-1 px-4 py-3 bg-[#4a3a5a] text-white rounded-lg hover:bg-[#3a2a4a] transition-colors text-sm whitespace-nowrap cursor-pointer"
                >
                  잠금 해제
                </button>
              </div>

              <div className="pt-4 border-t border-[#e8dfd0]">
                <p className="text-xs text-[#8b7d6f] text-center mb-3">
                  💎 프리미엄 구독 혜택
                </p>
                <ul className="text-xs text-[#6b5d4f] space-y-1 mb-3">
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-[#4a3a5a]"></i>
                    <span>AI 기반 히트 예측 리포트</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-[#4a3a5a]"></i>
                    <span>장르별 성공 확률 분석</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-[#4a3a5a]"></i>
                    <span>실패 사례 부검 리포트</span>
                  </li>
                </ul>
                <button className="w-full px-4 py-2 bg-gradient-to-r from-[#5a4a3a] to-[#4a3a2a] text-white rounded-lg hover:from-[#4a3a2a] hover:to-[#3a2a1a] transition-all duration-300 text-xs whitespace-nowrap cursor-pointer shadow-md">
                  프리미엄 구독 신청하기
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
